/* eslint-disable react-refresh/only-export-components */
import {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  ReactNode,
  useState,
  useMemo,
} from 'react';
import {
  Property,
  RoomType,
  CreatePropertyInput,
  UpdatePropertyInput,
  CreateRoomTypeInput,
  UpdateRoomTypeInput,
} from './types';
import { supabase } from '../../services/supabaseClient';
import { resolveFunctionsHttpError } from '../../services/edgeFunctionErrors';
import { setActiveProperty } from '../webhotelier/services/webHotelierProxyService';
import { useUser } from '../../app/providers/UserContext';

/**
 * Property Context State
 */
interface PropertyState {
  selectedProperty: Property | null;
  properties: Property[];
  roomTypes: RoomType[];
  isLoading: boolean;
  error: string | null;
}

/**
 * Property Context Actions
 */
type PropertyAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_SELECTED_PROPERTY'; payload: Property | null }
  | { type: 'SET_PROPERTIES'; payload: Property[] }
  | { type: 'ADD_PROPERTY'; payload: Property }
  | {
      type: 'UPDATE_PROPERTY';
      payload: { id: string; updates: Partial<Property> };
    }
  | { type: 'DELETE_PROPERTY'; payload: string }
  | { type: 'SET_ROOM_TYPES'; payload: RoomType[] }
  | { type: 'ADD_ROOM_TYPE'; payload: RoomType }
  | {
      type: 'UPDATE_ROOM_TYPE';
      payload: { id: string; updates: Partial<RoomType> };
    }
  | { type: 'DELETE_ROOM_TYPE'; payload: string };

/**
 * Property Context Value
 */
interface PropertyContextValue {
  // State
  selectedProperty: Property | null;
  properties: Property[];
  roomTypes: RoomType[];
  isLoading: boolean;
  error: string | null;
  isSyncingFromWebHotelier: boolean;

  // Property Actions
  selectProperty: (property: Property | null) => void;
  loadProperties: () => Promise<void>;
  loadPropertiesFromWebHotelier: () => Promise<void>;
  createProperty: (input: CreatePropertyInput) => Promise<Property>;
  updateProperty: (id: string, updates: UpdatePropertyInput) => Promise<void>;
  deleteProperty: (id: string) => Promise<void>;

  // Room Type Actions
  loadRoomTypes: (propertyId: string) => Promise<void>;
  createRoomType: (input: CreateRoomTypeInput) => Promise<RoomType>;
  updateRoomType: (id: string, updates: UpdateRoomTypeInput) => Promise<void>;
  deleteRoomType: (id: string) => Promise<void>;
}

/**
 * Property Reducer
 */
const propertyReducer = (
  state: PropertyState,
  action: PropertyAction
): PropertyState => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };

    case 'SET_ERROR':
      return { ...state, error: action.payload, isLoading: false };

    case 'SET_SELECTED_PROPERTY':
      return { ...state, selectedProperty: action.payload };

    case 'SET_PROPERTIES':
      return { ...state, properties: action.payload, isLoading: false };

    case 'ADD_PROPERTY':
      return {
        ...state,
        properties: [...state.properties, action.payload],
        isLoading: false,
      };

    case 'UPDATE_PROPERTY':
      return {
        ...state,
        properties: state.properties.map((prop) =>
          prop.id === action.payload.id
            ? { ...prop, ...action.payload.updates }
            : prop
        ),
        selectedProperty:
          state.selectedProperty?.id === action.payload.id
            ? { ...state.selectedProperty, ...action.payload.updates }
            : state.selectedProperty,
        isLoading: false,
      };

    case 'DELETE_PROPERTY':
      return {
        ...state,
        properties: state.properties.filter(
          (prop) => prop.id !== action.payload
        ),
        selectedProperty:
          state.selectedProperty?.id === action.payload
            ? null
            : state.selectedProperty,
        isLoading: false,
      };

    case 'SET_ROOM_TYPES':
      return { ...state, roomTypes: action.payload, isLoading: false };

    case 'ADD_ROOM_TYPE':
      return {
        ...state,
        roomTypes: [...state.roomTypes, action.payload],
        isLoading: false,
      };

    case 'UPDATE_ROOM_TYPE':
      return {
        ...state,
        roomTypes: state.roomTypes.map((room) =>
          room.id === action.payload.id
            ? { ...room, ...action.payload.updates }
            : room
        ),
        isLoading: false,
      };

    case 'DELETE_ROOM_TYPE':
      return {
        ...state,
        roomTypes: state.roomTypes.filter((room) => room.id !== action.payload),
        isLoading: false,
      };

    default:
      return state;
  }
};

/**
 * Initial State
 */
const initialState: PropertyState = {
  selectedProperty: null,
  properties: [],
  roomTypes: [],
  isLoading: false,
  error: null,
};

/**
 * Property Context
 */
const PropertyContext = createContext<PropertyContextValue | undefined>(
  undefined
);

/**
 * Property Provider Component
 */
export const PropertyProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(propertyReducer, initialState);
  const [isSyncingFromWebHotelier, setIsSyncingFromWebHotelier] =
    useState(false);
  const [hasLoadedProperties, setHasLoadedProperties] = useState(false);
  const { isLoading: isAuthLoading } = useUser();

  /**
   * Load Room Types for a property
   * First tries Supabase, then falls back to WebHotelier API if empty
   */
  const loadRoomTypes = useCallback(async (propertyId: string) => {
    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      // First try to load from Supabase
      const { data, error } = await supabase
        .from('room_types')
        .select('*')
        .eq('property_id', propertyId)
        .order('name');

      if (!error && data && data.length > 0) {
        const roomTypes: RoomType[] = data.map(
          (item: Record<string, unknown>) => ({
            id: item.id as string,
            name: item.name as string,
            code: item.code as string,
            description: item.description as string,
            capacity: (item.capacity as {
              adults: number;
              children: number;
              total: number;
            }) || { adults: 2, children: 0, total: 2 },
            size: item.size as number,
            amenities: (item.amenities as string[]) || [],
            basePrice: (item.base_price as number) || 0,
            propertyId: item.property_id as string,
          })
        );

        dispatch({ type: 'SET_ROOM_TYPES', payload: roomTypes });
        return;
      }

      // If Supabase is empty, try to fetch from WebHotelier API
      console.log(
        '[PropertyContext] No room types in Supabase, trying WebHotelier API...'
      );

      // Check whether an active WebHotelier integration exists for this
      // property without pulling credentials into browser memory. We only
      // need to know it's there so we can decide which code path to take;
      // the actual credentials are looked up server-side by the Edge
      // Function via propertyId.
      const { data: integration } = await supabase
        .from('pms_integrations')
        .select('id, config->>propertyCode')
        .eq('property_id', propertyId)
        .eq('pms_type', 'webhotelier')
        .eq('active', true)
        .single();

      if (!integration) {
        // No WebHotelier integration, check by webhotelier_code
        const { data: property } = await supabase
          .from('properties')
          .select('webhotelier_code')
          .eq('id', propertyId)
          .single();

        if (!property?.webhotelier_code) {
          console.log(
            '[PropertyContext] No WebHotelier config found for property'
          );
          dispatch({ type: 'SET_ROOM_TYPES', payload: [] });
          return;
        }

        // No pms_integrations row for this property. We used to fall back to
        // VITE_WEBHOTELIER_* env vars here, but Vite inlines those into the
        // browser bundle, exposing credentials to anyone who opens DevTools
        // (and they get baked into Docker image layers too). Credentials now
        // live only in pms_integrations — if a property has no row, we simply
        // can't load WebHotelier-backed room types for it.
        console.log(
          '[PropertyContext] No WebHotelier integration configured for property',
          propertyId
        );
        dispatch({ type: 'SET_ROOM_TYPES', payload: [] });
        return;
      } else {
        // Pass propertyId only — the Edge Function looks up credentials
        // from pms_integrations server-side after enforcing user→property
        // ownership. No password ever touches the browser here.
        const { data: response, error } = await supabase.functions.invoke(
          'webhotelier-api',
          {
            body: {
              action: 'getRoomTypes',
              propertyId,
            },
          }
        );

        if (error) {
          console.error(
            '[PropertyContext] Error loading room types:',
            await resolveFunctionsHttpError(error)
          );
        }

        if (response?.success && response?.data) {
          const roomTypesData = response.data.data || response.data;
          const roomsArray = roomTypesData?.rooms || roomTypesData;

          if (Array.isArray(roomsArray) && roomsArray.length > 0) {
            const roomTypes: RoomType[] = roomsArray.map(
              (room: Record<string, unknown>) => ({
                id: room.code as string,
                name: room.name as string,
                code: room.code as string,
                description: (room.description as string) || '',
                capacity: (room.capacity as {
                  adults: number;
                  children: number;
                  total: number;
                }) || { adults: 2, children: 0, total: 2 },
                size: room.size as number,
                amenities: (room.amenities as string[]) || [],
                basePrice: 0,
                propertyId: propertyId,
              })
            );

            console.log(
              `[PropertyContext] Loaded ${roomTypes.length} room types from WebHotelier (integration config)`
            );
            dispatch({ type: 'SET_ROOM_TYPES', payload: roomTypes });
            return;
          }
        }
      }

      dispatch({ type: 'SET_ROOM_TYPES', payload: [] });
    } catch (error) {
      console.error('Error loading room types:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load room types' });
    }
  }, []);

  /**
   * Select Property
   */
  const selectProperty = useCallback(
    (property: Property | null) => {
      dispatch({ type: 'SET_SELECTED_PROPERTY', payload: property });

      if (property) {
        setActiveProperty(property.id, property.code || property.id);
        loadRoomTypes(property.id);
      }
    },
    [loadRoomTypes]
  );

  /**
   * Load Properties from WebHotelier API
   * Fetches all active WebHotelier integrations and loads property data from the API
   */
  const loadPropertiesFromWebHotelier = useCallback(async () => {
    setIsSyncingFromWebHotelier(true);
    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      console.log(
        '[PropertyContext] Loading properties from WebHotelier API...'
      );

      // Get all active WebHotelier integrations. Project only propertyCode
      // out of config — credentials stay server-side and are fetched by the
      // webhotelier-api Edge Function via the secure-lookup path below.
      const { data: integrations, error: intError } = await supabase
        .from('pms_integrations')
        .select(
          'id, property_id, pms_type, active, propertyCode:config->>propertyCode'
        )
        .eq('pms_type', 'webhotelier')
        .eq('active', true);

      if (intError) {
        console.error(
          '[PropertyContext] Error fetching integrations:',
          intError
        );
        throw intError;
      }

      if (!integrations || integrations.length === 0) {
        console.log(
          '[PropertyContext] No active WebHotelier integrations found'
        );
        dispatch({ type: 'SET_LOADING', payload: false });
        setIsSyncingFromWebHotelier(false);
        return;
      }

      console.log(
        `[PropertyContext] Found ${integrations.length} active WebHotelier integrations`
      );

      const loadedProperties: Property[] = [];

      type IntegrationRow = {
        id: string;
        property_id: string;
        pms_type: string;
        active: boolean;
        propertyCode: string | null;
      };

      // Fetch property info for each integration
      for (const integration of (integrations || []) as IntegrationRow[]) {
        try {
          const propertyCode = integration.propertyCode;
          if (!propertyCode) {
            console.warn(
              `[PropertyContext] Integration ${integration.id} missing property code, skipping`
            );
            continue;
          }

          console.log(
            `[PropertyContext] Fetching property ${propertyCode} from WebHotelier...`
          );
          console.log(
            `[PropertyContext] Integration property_id: ${integration.property_id}`
          );

          // Check if property_id is a valid UUID or a WebHotelier code
          // If it's not a valid UUID, look up the property by webhotelier_code
          let databasePropertyId = integration.property_id;
          const uuidRegex =
            /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

          if (!uuidRegex.test(integration.property_id)) {
            console.log(
              `[PropertyContext] property_id "${integration.property_id}" is not a UUID, looking up by webhotelier_code...`
            );
            const { data: existingProperty } = await supabase
              .from('properties')
              .select('id')
              .eq('webhotelier_code', propertyCode)
              .single();

            if (existingProperty?.id) {
              databasePropertyId = existingProperty.id;
              console.log(
                `[PropertyContext] Found property UUID: ${databasePropertyId}`
              );
            } else {
              console.log(
                `[PropertyContext] No property found with webhotelier_code ${propertyCode}, will use WebHotelier code as ID`
              );
            }
          }

          // Call the edge function to get property info. Pass propertyId only
          // — the function does the credential lookup server-side after
          // verifying the caller is assigned to this property.
          const { data: response, error: apiError } =
            await supabase.functions.invoke('webhotelier-api', {
              body: {
                action: 'getPropertyInfo',
                propertyId: integration.property_id,
              },
            });

          if (apiError) {
            console.error(
              `[PropertyContext] Error fetching property ${propertyCode}:`,
              await resolveFunctionsHttpError(apiError)
            );
            continue;
          }

          if (!response?.success || !response?.data) {
            console.warn(
              `[PropertyContext] No data returned for property ${propertyCode}`
            );
            continue;
          }

          // WebHotelier wraps response in { data: { ... } }
          const webHotelierData = response.data.data || response.data;
          console.log(
            `[PropertyContext] WebHotelier property data:`,
            webHotelierData
          );

          // Fetch room types to get total room count. Same secure-lookup
          // pattern — propertyId only, no credentials in browser.
          let totalRooms = 0;
          try {
            const { data: roomTypesResponse, error: roomTypesError } =
              await supabase.functions.invoke('webhotelier-api', {
                body: {
                  action: 'getRoomTypes',
                  propertyId: integration.property_id,
                },
              });

            if (roomTypesError) {
              console.warn(
                `[PropertyContext] Error fetching room types for ${propertyCode}:`,
                await resolveFunctionsHttpError(roomTypesError)
              );
            }

            if (roomTypesResponse?.success && roomTypesResponse?.data) {
              const roomTypesData =
                roomTypesResponse.data.data || roomTypesResponse.data;
              const roomsArray = roomTypesData?.rooms || roomTypesData;

              if (Array.isArray(roomsArray)) {
                // Sum up inventory/count from each room type
                totalRooms = roomsArray.reduce(
                  (sum: number, room: Record<string, unknown>) => {
                    // WebHotelier may return inventory, count, or quantity
                    const roomCount =
                      room.inventory ||
                      room.count ||
                      room.quantity ||
                      room.total ||
                      1;
                    return (
                      sum + (typeof roomCount === 'number' ? roomCount : 1)
                    );
                  },
                  0
                );
                console.log(
                  `[PropertyContext] Total rooms from ${roomsArray.length} room types: ${totalRooms}`
                );
              }
            }
          } catch (roomErr) {
            console.warn(
              `[PropertyContext] Could not fetch room types for ${propertyCode}:`,
              roomErr
            );
          }

          // Normalize facilities - WebHotelier may return objects or strings
          let normalizedFacilities: string[] = [];
          if (Array.isArray(webHotelierData.facilities)) {
            normalizedFacilities = webHotelierData.facilities.map(
              (f: unknown) => {
                if (typeof f === 'string') return f;
                if (typeof f === 'object' && f !== null && 'name' in f)
                  return (f as { name: string }).name;
                return String(f);
              }
            );
          }

          // Normalize images - ensure they have the correct structure
          const normalizedImages = Array.isArray(webHotelierData.images)
            ? webHotelierData.images.map((img: unknown) => {
                if (typeof img === 'object' && img !== null) {
                  const imgObj = img as Record<string, unknown>;
                  return {
                    url: String(imgObj.url || imgObj.src || ''),
                    caption: imgObj.caption
                      ? String(imgObj.caption)
                      : undefined,
                    type: imgObj.type ? String(imgObj.type) : undefined,
                  };
                }
                return { url: String(img) };
              })
            : [];

          // Helper to safely get string value
          const safeString = (value: unknown, fallback = ''): string => {
            if (typeof value === 'string') return value;
            if (typeof value === 'number') return String(value);
            if (value === null || value === undefined) return fallback;
            return fallback;
          };

          // Determine property ID - prefer database UUID over WebHotelier code
          const propertyId =
            safeString(databasePropertyId) || safeString(webHotelierData.code);
          console.log(
            `[PropertyContext] Using property ID: ${propertyId} (database: ${databasePropertyId}, from WH: ${webHotelierData.code})`
          );

          // Create property object with safe string conversions
          const property: Property = {
            id: propertyId,
            name: safeString(webHotelierData.name, 'Unknown Property'),
            location:
              safeString(webHotelierData.city) ||
              safeString(webHotelierData.address) ||
              '',
            address: safeString(webHotelierData.address),
            rooms:
              totalRooms ||
              (typeof webHotelierData.rooms === 'number'
                ? webHotelierData.rooms
                : 0),
            occupancy: 0,
            adr: 0,
            revpar: 0,
            image: normalizedImages[0]?.url || '/placeholder-property.jpg',
            code: safeString(webHotelierData.code),
            webHotelierCode: safeString(webHotelierData.code),
            phone: safeString(webHotelierData.phone),
            email: safeString(webHotelierData.email),
            website: safeString(webHotelierData.website),
            description: safeString(webHotelierData.description),
            facilities: normalizedFacilities,
            images: normalizedImages,
            lastUpdated: new Date(),
            metricsSource: 'webhotelier',
          };

          // Update the property in the database (only if we have a valid UUID)
          if (databasePropertyId && uuidRegex.test(databasePropertyId)) {
            const { error: updateError } = await supabase
              .from('properties')
              .update({
                name: property.name,
                address: property.address,
                city: property.location,
                rooms: property.rooms,
                phone: property.phone,
                email: property.email,
                website: property.website,
                description: property.description,
                facilities: property.facilities,
                webhotelier_code: property.webHotelierCode,
                updated_at: new Date().toISOString(),
              })
              .eq('id', databasePropertyId);

            if (updateError) {
              console.warn(
                `[PropertyContext] Failed to update property in DB:`,
                updateError
              );
            } else {
              console.log(
                `[PropertyContext] Property ${property.name} updated in database`
              );
            }
          } else {
            console.log(
              `[PropertyContext] Skipping DB update - no valid UUID for property ${property.code}`
            );
          }

          loadedProperties.push(property);
          console.log(
            `[PropertyContext] Loaded property: ${property.name} (${property.code})`
          );
        } catch (propError) {
          console.error(
            `[PropertyContext] Error processing integration ${integration.id}:`,
            propError
          );
        }
      }

      if (loadedProperties.length > 0) {
        console.log(
          `[PropertyContext] Successfully loaded ${loadedProperties.length} properties from WebHotelier`
        );
        dispatch({ type: 'SET_PROPERTIES', payload: loadedProperties });

        // Auto-select first property if none selected
        const firstProperty = loadedProperties[0];
        if (!state.selectedProperty && firstProperty) {
          setActiveProperty(
            firstProperty.id,
            firstProperty.code || firstProperty.id
          );
          dispatch({ type: 'SET_SELECTED_PROPERTY', payload: firstProperty });
        }
      } else {
        console.warn(
          '[PropertyContext] No properties could be loaded from WebHotelier'
        );
      }

      dispatch({ type: 'SET_LOADING', payload: false });
    } catch (error) {
      console.error(
        '[PropertyContext] Error loading properties from WebHotelier:',
        error
      );
      dispatch({
        type: 'SET_ERROR',
        payload: 'Failed to load properties from WebHotelier',
      });
    } finally {
      setIsSyncingFromWebHotelier(false);
    }
  }, [state.selectedProperty]);

  /**
   * Load Properties - loads from Supabase database (synced via Edge Functions)
   */
  const loadProperties = useCallback(async () => {
    console.log('[PropertyContext] loadProperties() called');
    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      console.log('[PropertyContext] Querying Supabase for properties...');

      const startTime = Date.now();

      // Use Supabase client with authenticated session for RLS policies
      const { data, error } = await supabase
        .from('properties')
        .select('*')
        .eq('active', true)
        .order('name');

      const duration = Date.now() - startTime;
      console.log(`[PropertyContext] Query completed in ${duration}ms`);

      if (error) {
        console.error('[PropertyContext] Supabase error:', error);
        throw error;
      }

      console.log('[PropertyContext] Data received:', {
        dataLength: data?.length,
        sample: data?.[0],
      });

      if (data && data.length > 0) {
        console.log('[PropertyContext] Found properties in Supabase:', data);
        const properties: Property[] = data.map((row) => {
          const item = row as Record<string, unknown>;
          // Normalize facilities - may contain objects from WebHotelier sync
          let normalizedFacilities: string[] = [];
          if (Array.isArray(item['facilities'])) {
            normalizedFacilities = (item['facilities'] as unknown[])
              .map((f: unknown) => {
                if (typeof f === 'string') return f;
                if (typeof f === 'object' && f !== null && 'name' in f)
                  return (f as { name: string }).name;
                return String(f);
              })
              .filter(Boolean);
          }

          return {
            id: String(item['id']),
            name: typeof item['name'] === 'string' ? item['name'] : 'Unknown',
            location:
              typeof item['city'] === 'string'
                ? item['city']
                : typeof item['location'] === 'string'
                  ? item['location']
                  : '',
            address: typeof item['address'] === 'string' ? item['address'] : '',
            rooms: typeof item['rooms'] === 'number' ? item['rooms'] : 0,
            occupancy:
              typeof item['occupancy'] === 'number' ? item['occupancy'] : 0,
            adr: typeof item['adr'] === 'number' ? item['adr'] : 0,
            revpar: typeof item['revpar'] === 'number' ? item['revpar'] : 0,
            image:
              typeof item['image'] === 'string'
                ? item['image']
                : '/placeholder-property.jpg',
            code:
              typeof item['code'] === 'string'
                ? item['code']
                : typeof item['webhotelier_code'] === 'string'
                  ? item['webhotelier_code']
                  : 'UNKNOWN',
            webHotelierCode:
              typeof item['webhotelier_code'] === 'string'
                ? item['webhotelier_code']
                : undefined,
            phone:
              typeof item['phone'] === 'string' ? item['phone'] : undefined,
            email:
              typeof item['email'] === 'string' ? item['email'] : undefined,
            website:
              typeof item['website'] === 'string' ? item['website'] : undefined,
            description:
              typeof item['description'] === 'string'
                ? item['description']
                : undefined,
            facilities: normalizedFacilities,
          };
        });

        dispatch({ type: 'SET_PROPERTIES', payload: properties });

        // Auto-select first property
        const firstProperty = properties[0];
        if (firstProperty) {
          setActiveProperty(
            firstProperty.id,
            firstProperty.code || firstProperty.id
          );
          dispatch({ type: 'SET_SELECTED_PROPERTY', payload: firstProperty });
        }
        console.log(
          '[PropertyContext] Successfully loaded and selected property:',
          firstProperty?.name
        );
        return;
      }

      // Fallback to localStorage
      const savedData = localStorage.getItem('smartrate-data');
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        if (parsedData.properties && parsedData.properties.length > 0) {
          // Normalize localStorage properties to ensure no objects in renderable fields
          const normalizedLocalProperties = parsedData.properties.map(
            (p: Record<string, unknown>) => {
              let normalizedFacilities: string[] = [];
              if (Array.isArray(p.facilities)) {
                normalizedFacilities = p.facilities
                  .map((f: unknown) => {
                    if (typeof f === 'string') return f;
                    if (typeof f === 'object' && f !== null && 'name' in f)
                      return (f as { name: string }).name;
                    return '';
                  })
                  .filter(Boolean);
              }
              return {
                ...p,
                name: typeof p.name === 'string' ? p.name : 'Unknown',
                location: typeof p.location === 'string' ? p.location : '',
                address: typeof p.address === 'string' ? p.address : '',
                facilities: normalizedFacilities,
              };
            }
          );

          dispatch({
            type: 'SET_PROPERTIES',
            payload: normalizedLocalProperties,
          });

          // Auto-select from localStorage
          if (parsedData.selectedProperty) {
            const normalizedSelected = {
              ...parsedData.selectedProperty,
              name:
                typeof parsedData.selectedProperty.name === 'string'
                  ? parsedData.selectedProperty.name
                  : 'Unknown',
              location:
                typeof parsedData.selectedProperty.location === 'string'
                  ? parsedData.selectedProperty.location
                  : '',
            };
            setActiveProperty(
              normalizedSelected.id,
              normalizedSelected.code || normalizedSelected.id
            );
            dispatch({
              type: 'SET_SELECTED_PROPERTY',
              payload: normalizedSelected,
            });
          } else {
            const firstLocalProperty = normalizedLocalProperties[0];
            if (firstLocalProperty) {
              setActiveProperty(
                firstLocalProperty.id,
                firstLocalProperty.code || firstLocalProperty.id
              );
              dispatch({
                type: 'SET_SELECTED_PROPERTY',
                payload: firstLocalProperty,
              });
            }
          }
          return;
        }
      }

      // No properties found - use demo data for development
      console.log(
        '[PropertyContext] No properties found in Supabase or localStorage, using demo data'
      );
      const demoProperties: Property[] = [
        {
          id: 'demo-property-1',
          name: 'Kottas Hotel Demo',
          location: 'Athens, Greece',
          address: 'Demo Address 123',
          rooms: 50,
          occupancy: 75,
          adr: 120.5,
          revpar: 90.38,
          image: '/placeholder-property.jpg',
          code: 'PHILORTEST',
          webHotelierCode: 'PHILORTEST',
          phone: '+30 210 1234567',
          email: 'demo@kottashotel.com',
          website: 'https://demo.com',
          description: 'Demo property for development',
          facilities: ['WiFi', 'Pool', 'Gym', 'Restaurant'],
        },
      ];

      dispatch({ type: 'SET_PROPERTIES', payload: demoProperties });
      if (demoProperties[0]) {
        setActiveProperty(
          demoProperties[0].id,
          demoProperties[0].code || demoProperties[0].id
        );
      }
      dispatch({ type: 'SET_SELECTED_PROPERTY', payload: demoProperties[0] });
      dispatch({ type: 'SET_LOADING', payload: false });
    } catch (error) {
      console.error('Error loading properties:', error);

      // Try localStorage fallback on error
      try {
        const savedData = localStorage.getItem('smartrate-data');
        if (savedData) {
          const parsedData = JSON.parse(savedData);
          if (parsedData.properties && parsedData.properties.length > 0) {
            console.log(
              '[PropertyContext] Using localStorage fallback after error'
            );
            dispatch({
              type: 'SET_PROPERTIES',
              payload: parsedData.properties,
            });
            if (parsedData.selectedProperty) {
              setActiveProperty(
                parsedData.selectedProperty.id,
                parsedData.selectedProperty.code ||
                  parsedData.selectedProperty.id
              );
              dispatch({
                type: 'SET_SELECTED_PROPERTY',
                payload: parsedData.selectedProperty,
              });
            } else {
              const firstProperty = parsedData.properties[0];
              if (firstProperty) {
                setActiveProperty(
                  firstProperty.id,
                  firstProperty.code || firstProperty.id
                );
                dispatch({
                  type: 'SET_SELECTED_PROPERTY',
                  payload: firstProperty,
                });
              }
            }
            return;
          }
        }
      } catch (localErr) {
        console.error('Error reading localStorage:', localErr);
      }

      // Final fallback to demo data
      console.log('[PropertyContext] Using demo data fallback after error');
      const demoProperties: Property[] = [
        {
          id: 'demo-property-1',
          name: 'Kottas Hotel Demo',
          location: 'Athens, Greece',
          address: 'Demo Address 123',
          rooms: 50,
          occupancy: 75,
          adr: 120.5,
          revpar: 90.38,
          image: '/placeholder-property.jpg',
          code: 'PHILORTEST',
          webHotelierCode: 'PHILORTEST',
          phone: '+30 210 1234567',
          email: 'demo@kottashotel.com',
          website: 'https://demo.com',
          description: 'Demo property for development',
          facilities: ['WiFi', 'Pool', 'Gym', 'Restaurant'],
        },
      ];
      dispatch({ type: 'SET_PROPERTIES', payload: demoProperties });
      const firstDemoProperty = demoProperties[0];
      if (firstDemoProperty) {
        setActiveProperty(
          firstDemoProperty.id,
          firstDemoProperty.code || firstDemoProperty.id
        );
        dispatch({ type: 'SET_SELECTED_PROPERTY', payload: firstDemoProperty });
      }
      dispatch({
        type: 'SET_ERROR',
        payload: 'Failed to load properties from server, using demo data',
      });
    }
  }, []);

  /**
   * Create Property
   */
  const createProperty = useCallback(
    async (input: CreatePropertyInput): Promise<Property> => {
      dispatch({ type: 'SET_LOADING', payload: true });

      try {
        const { data, error } = await supabase
          .from('properties')
          .insert([
            {
              name: input.name,
              location: input.location,
              address: input.address,
              rooms: input.rooms,
              code: input.code,
              webhotelier_code: input.code,
              phone: input.phone,
              email: input.email,
              website: input.website,
              description: input.description,
            },
          ])
          .select()
          .single();

        if (error) throw error;

        const property: Property = {
          id: data.id,
          name: data.name,
          location: data.location,
          address: data.address,
          rooms: data.rooms,
          occupancy: 0,
          adr: 0,
          revpar: 0,
          image: '/placeholder-property.jpg',
          code: data.code,
          webHotelierCode: data.webhotelier_code,
          phone: data.phone,
          email: data.email,
          website: data.website,
          description: data.description,
        };

        dispatch({ type: 'ADD_PROPERTY', payload: property });
        return property;
      } catch (error) {
        console.error('Error creating property:', error);
        dispatch({ type: 'SET_ERROR', payload: 'Failed to create property' });
        throw error;
      }
    },
    []
  );

  /**
   * Update Property
   */
  const updateProperty = useCallback(
    async (id: string, updates: UpdatePropertyInput) => {
      dispatch({ type: 'SET_LOADING', payload: true });

      try {
        const { error } = await supabase
          .from('properties')
          .update({
            name: updates.name,
            location: updates.location,
            address: updates.address,
            rooms: updates.rooms,
            code: updates.code,
            webhotelier_code: updates.code,
            phone: updates.phone,
            email: updates.email,
            website: updates.website,
            description: updates.description,
            facilities: updates.facilities,
          })
          .eq('id', id);

        if (error) throw error;

        dispatch({ type: 'UPDATE_PROPERTY', payload: { id, updates } });
      } catch (error) {
        console.error('Error updating property:', error);
        dispatch({ type: 'SET_ERROR', payload: 'Failed to update property' });
        throw error;
      }
    },
    []
  );

  /**
   * Delete Property
   */
  const deleteProperty = useCallback(
    async (id: string) => {
      dispatch({ type: 'SET_LOADING', payload: true });

      try {
        const { error } = await supabase.rpc('delete_property_cascade', {
          p_property_id: id,
        });

        if (error) throw error;

        dispatch({ type: 'DELETE_PROPERTY', payload: id });

        // Auto-select another property if the deleted one was selected
        if (state.selectedProperty?.id === id) {
          const remaining = state.properties.filter((p) => p.id !== id);
          if (remaining.length > 0 && remaining[0]) {
            selectProperty(remaining[0]);
          }
        }
      } catch (error) {
        console.error('Error deleting property:', error);
        dispatch({ type: 'SET_ERROR', payload: 'Failed to delete property' });
        throw error;
      }
    },
    [state.selectedProperty, state.properties, selectProperty]
  );

  /**
   * Create Room Type
   */
  const createRoomType = useCallback(
    async (input: CreateRoomTypeInput): Promise<RoomType> => {
      dispatch({ type: 'SET_LOADING', payload: true });

      try {
        const { data, error } = await supabase
          .from('room_types')
          .insert([
            {
              name: input.name,
              capacity: input.capacity,
              base_price: input.basePrice,
              property_id: input.propertyId,
              description: input.description,
              size: input.size,
              amenities: input.amenities || [],
            },
          ])
          .select()
          .single();

        if (error) throw error;

        const roomType: RoomType = {
          id: data.id,
          name: data.name,
          capacity: data.capacity,
          basePrice: data.base_price,
          propertyId: data.property_id,
          description: data.description,
          size: data.size,
          amenities: data.amenities || [],
        };

        dispatch({ type: 'ADD_ROOM_TYPE', payload: roomType });
        return roomType;
      } catch (error) {
        console.error('Error creating room type:', error);
        dispatch({ type: 'SET_ERROR', payload: 'Failed to create room type' });
        throw error;
      }
    },
    []
  );

  /**
   * Update Room Type
   */
  const updateRoomType = useCallback(
    async (id: string, updates: UpdateRoomTypeInput) => {
      dispatch({ type: 'SET_LOADING', payload: true });

      try {
        const { error } = await supabase
          .from('room_types')
          .update({
            name: updates.name,
            capacity: updates.capacity,
            base_price: updates.basePrice,
            description: updates.description,
            size: updates.size,
            amenities: updates.amenities,
          })
          .eq('id', id);

        if (error) throw error;

        dispatch({ type: 'UPDATE_ROOM_TYPE', payload: { id, updates } });
      } catch (error) {
        console.error('Error updating room type:', error);
        dispatch({ type: 'SET_ERROR', payload: 'Failed to update room type' });
        throw error;
      }
    },
    []
  );

  /**
   * Delete Room Type
   */
  const deleteRoomType = useCallback(async (id: string) => {
    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      const { error } = await supabase.from('room_types').delete().eq('id', id);

      if (error) throw error;

      dispatch({ type: 'DELETE_ROOM_TYPE', payload: id });
    } catch (error) {
      console.error('Error deleting room type:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to delete room type' });
      throw error;
    }
  }, []);

  // Load properties when auth is ready
  useEffect(() => {
    if (hasLoadedProperties) return;

    // Wait for auth to finish loading
    if (isAuthLoading) return;

    // Auth finished - load properties
    setHasLoadedProperties(true);
    loadProperties();
  }, [isAuthLoading, hasLoadedProperties, loadProperties]);

  const value = useMemo<PropertyContextValue>(
    () => ({
      selectedProperty: state.selectedProperty,
      properties: state.properties,
      roomTypes: state.roomTypes,
      isLoading: state.isLoading,
      error: state.error,
      isSyncingFromWebHotelier,
      selectProperty,
      loadProperties,
      loadPropertiesFromWebHotelier,
      createProperty,
      updateProperty,
      deleteProperty,
      loadRoomTypes,
      createRoomType,
      updateRoomType,
      deleteRoomType,
    }),
    [
      state.selectedProperty,
      state.properties,
      state.roomTypes,
      state.isLoading,
      state.error,
      isSyncingFromWebHotelier,
      selectProperty,
      loadProperties,
      loadPropertiesFromWebHotelier,
      createProperty,
      updateProperty,
      deleteProperty,
      loadRoomTypes,
      createRoomType,
      updateRoomType,
      deleteRoomType,
    ]
  );

  return (
    <PropertyContext.Provider value={value}>
      {children}
    </PropertyContext.Provider>
  );
};

/**
 * useProperty Hook
 * Custom hook to access Property context
 */
export const useProperty = (): PropertyContextValue => {
  const context = useContext(PropertyContext);

  if (context === undefined) {
    throw new Error('useProperty must be used within a PropertyProvider');
  }

  return context;
};

export default PropertyContext;
